import React, { useEffect, useRef, useState } from "react";
import {
  FaFileExcel,
  FaFilePdf,
  FaDownload,
  FaFilter,
  FaSyncAlt,
  FaTimes,
} from "react-icons/fa";

import { PAYMENT_STATUS, PAYMENT_TYPES } from "../utils/paymentsDashboardUtils";

export default function PaymentsFiltersCard({
  filters,
  setFilter,
  resetFilters,
  toggleStatusFilter,
  selectAllStatuses,
  clearStatusFilters,
  articleOptions,
  seasons,
  filteredCount,
  totalCount,
  loading,
  onRefresh,
  onExport,
}) {
  const [statusOpen, setStatusOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);
  const statusRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (!statusRef.current) return;
      if (!statusRef.current.contains(event.target)) {
        setStatusOpen(false);
      }
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setExportOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);

    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const hasFilters =
    filters.query.trim() ||
    filters.article ||
    filters.statuses.length ||
    filters.type ||
    filters.season ||
    filters.kind ||
    filters.authorization;

  const statusLabel =
    filters.statuses.length === 0
      ? "Statut : tous"
      : `Statut : ${filters.statuses.length}`;

  return (
    <section className="relative overflow-visible rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 rounded-t-[28px] bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Filtres
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaFilter className="text-indigo-600 dark:text-indigo-300" />
              Recherche paiements
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Filtrez par saison, adhérent, utilisateur, article, statut, moyen
              de paiement, type d’achat ou visibilité côté adhérent.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {hasFilters ? (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Réinitialiser
              </button>
            ) : null}

            <div className="relative" ref={exportRef}>
              <button
                type="button"
                disabled={loading || filteredCount === 0}
                onClick={() => setExportOpen((prev) => !prev)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaDownload />
                Exporter
              </button>

              {exportOpen && (
                <div className="absolute right-0 top-full z-[9999] mt-2 w-52 overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-xl dark:border-slate-700 dark:bg-slate-950">
                  <button
                    type="button"
                    onClick={() => {
                      setExportOpen(false);
                      onExport("excel");
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-slate-700 transition hover:bg-emerald-50 dark:text-slate-200 dark:hover:bg-emerald-500/10"
                  >
                    <FaFileExcel className="text-lg text-emerald-600" />
                    Export Excel
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setExportOpen(false);
                      onExport("pdf");
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-slate-700 transition hover:bg-rose-50 dark:text-slate-200 dark:hover:bg-rose-500/10"
                  >
                    <FaFilePdf className="text-lg text-rose-600" />
                    Export PDF
                  </button>

                  <div className="mt-1 border-t border-slate-100 px-3 py-2 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                    {filteredCount} paiement(s) sélectionné(s)
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              Actualiser
            </button>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-7">
          <input
            type="text"
            value={filters.query}
            onChange={(event) => setFilter("query", event.target.value)}
            placeholder="Nom, adhérent, email, message..."
            className="xl:col-span-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
          />

          <select
            value={filters.article}
            onChange={(event) => setFilter("article", event.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
          >
            <option value="">Article : tous</option>
            <option value="Règlement Inscription">Règlement Inscription</option>

            {articleOptions.map((article) => (
              <option key={article.id} value={article.value}>
                {article.label}
              </option>
            ))}
          </select>

          <div className="relative" ref={statusRef}>
            <button
              type="button"
              onClick={() => setStatusOpen((prev) => !prev)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-left text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
            >
              {statusLabel}
            </button>

            {statusOpen ? (
              <div className="absolute left-0 top-full z-[9999] mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl dark:border-slate-700 dark:bg-slate-950">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={selectAllStatuses}
                    className="rounded-xl border border-slate-200 px-2 py-1 text-xs font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Tout
                  </button>

                  <button
                    type="button"
                    onClick={clearStatusFilters}
                    className="rounded-xl border border-slate-200 px-2 py-1 text-xs font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Effacer
                  </button>
                </div>

                <div className="max-h-[360px] space-y-1 overflow-y-auto pr-1">
                  {PAYMENT_STATUS.map((status) => {
                    const id = `payment-status-${status}`;
                    const checked = filters.statuses.includes(status);

                    return (
                      <label
                        key={status}
                        htmlFor={id}
                        className="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        <input
                          id={id}
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleStatusFilter(status)}
                          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                        />

                        <span className="min-w-0 flex-1 truncate">
                          {status}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>

          <select
            value={filters.type}
            onChange={(event) => setFilter("type", event.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
          >
            <option value="">Règlement : tous</option>
            {PAYMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>

          <select
            value={filters.season}
            onChange={(event) => setFilter("season", event.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
          >
            <option value="">Saison : toutes</option>

            {seasons.map((season) => (
              <option key={season} value={season}>
                {season}
              </option>
            ))}
          </select>

          <select
            value={filters.kind}
            onChange={(event) => setFilter("kind", event.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
          >
            <option value="">Type achat : tous</option>
            <option value="boutique">Boutique</option>
            <option value="licence">Licence / cotisation</option>
          </select>

          <select
            value={filters.authorization}
            onChange={(event) => setFilter("authorization", event.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
          >
            <option value="">Autorisation : tous</option>
            <option value="visible">Visible adhérent</option>
            <option value="hidden">Masqué adhérent</option>
          </select>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-600 dark:text-slate-300">
          <span className="rounded-full bg-slate-100 px-3 py-1 font-bold dark:bg-slate-800">
            {filteredCount} / {totalCount} paiement(s)
          </span>

          {filters.statuses.length ? (
            filters.statuses.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => toggleStatusFilter(status)}
                className="rounded-full bg-indigo-50 px-3 py-1 font-bold text-indigo-700 transition hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300"
              >
                {status} ×
              </button>
            ))
          ) : (
            <span className="rounded-full bg-slate-100 px-3 py-1 font-bold dark:bg-slate-800">
              Tous statuts
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
